import { useMemo, useState } from 'react';
import {
  respondToRollCall,
  statusLabel,
  type RollCall,
  type RollCallResponse,
  type RollCallStatus,
} from '../lib/rollcall';
import type { CrewMember } from '../state/session';
import { colors } from '../theme';

// Port of app/src/components/RollCallOverlay.tsx. Full-screen "Are you okay?"
// prompt for members who haven't responded; slim status banner for everyone
// else (with tally + 🆘 callouts for the starter, change-answer chips for
// already-responded members).

type Props = {
  rollCall: RollCall | null;
  responses: RollCallResponse[];
  crew: CrewMember[];
  selfId: string;
};

const CHOICES: { status: RollCallStatus; tone: string }[] = [
  { status: 'ok', tone: colors.accent },
  { status: 'need_help', tone: colors.danger },
  { status: 'on_my_way', tone: colors.alt },
  { status: 'busy', tone: colors.textMuted },
];

export function RollCallOverlay({ rollCall, responses, crew, selfId }: Props) {
  const [submitting, setSubmitting] = useState<RollCallStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { myResponse, otherResponses, pending } = useMemo(() => {
    if (!rollCall) {
      return {
        myResponse: null as RollCallResponse | null,
        otherResponses: [] as RollCallResponse[],
        pending: [] as CrewMember[],
      };
    }
    const mine = responses.find((r) => r.user_id === selfId) ?? null;
    const others = responses.filter((r) => r.user_id !== selfId);
    const respondedIds = new Set(responses.map((r) => r.user_id));
    const pendingMembers = crew.filter(
      (c) => c.id !== rollCall.started_by && !respondedIds.has(c.id)
    );
    return { myResponse: mine, otherResponses: others, pending: pendingMembers };
  }, [rollCall, responses, crew, selfId]);

  if (!rollCall) return null;
  const isStarter = rollCall.started_by === selfId;
  const needsResponse = !isStarter && !myResponse;

  async function respond(status: RollCallStatus) {
    if (!rollCall) return;
    setSubmitting(status);
    setError(null);
    try {
      await respondToRollCall(rollCall.id, selfId, status);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSubmitting(null);
    }
  }

  if (needsResponse) {
    return (
      <div className="rc-scrim">
        <div className="rc-card">
          <div className="rc-kicker">CHECK-IN</div>
          <div className="rc-title">Are you okay?</div>
          <div className="rc-btns">
            {CHOICES.map((c) => (
              <button
                key={c.status}
                className="rc-btn"
                style={{ borderColor: c.tone, color: c.tone }}
                disabled={submitting !== null}
                onClick={() => respond(c.status)}
              >
                {submitting === c.status ? '…' : statusLabel(c.status)}
              </button>
            ))}
          </div>
          {error && <div className="rc-error">{error}</div>}
        </div>

        <style>{`
          .rc-scrim {
            position: fixed; inset: 0;
            background: rgba(0,0,0,0.75);
            z-index: 2500;
            display: flex; align-items: center; justify-content: center;
            padding: 24px;
          }
          .rc-card {
            background: ${colors.surface};
            border-radius: 18px;
            padding: 24px;
            width: 100%;
            max-width: 380px;
            display: flex; flex-direction: column; align-items: center;
            gap: 14px;
          }
          .rc-kicker { color: ${colors.accent}; font-size: 13px; font-weight: 700; letter-spacing: 1.4px; }
          .rc-title { color: ${colors.text}; font-size: 22px; font-weight: 700; }
          .rc-btns { width: 100%; display: flex; flex-direction: column; gap: 10px; margin-top: 10px; }
          .rc-btn {
            width: 100%;
            border: 2px solid;
            background: transparent;
            border-radius: 12px;
            padding: 14px;
            font-size: 16px;
            font-weight: 700;
          }
          .rc-btn:disabled { opacity: 0.5; }
          .rc-error { color: ${colors.error}; font-size: 12px; text-align: center; }
        `}</style>
      </div>
    );
  }

  // Slim status banner.
  const total = crew.length - 1; // exclude starter
  const respondedCount = responses.length;
  const lines: string[] = [];
  if (isStarter) {
    lines.push(`Roll call · ${respondedCount}/${total} responded`);
    const needHelp = responses.filter((r) => r.status === 'need_help');
    if (needHelp.length) {
      lines.push(
        `🆘 ${needHelp
          .map((r) => crew.find((c) => c.id === r.user_id)?.display_name ?? '?')
          .join(', ')}`
      );
    }
    if (pending.length && pending.length <= 4) {
      lines.push(`Waiting: ${pending.map((c) => c.display_name).join(', ')}`);
    } else if (pending.length) {
      lines.push(`Waiting: ${pending.length} others`);
    }
  } else if (myResponse) {
    lines.push(`You replied: ${statusLabel(myResponse.status)}`);
  }

  return (
    <div className="rc-banner">
      {lines.map((l, i) => (
        <div key={i} className="rc-line">
          {l}
        </div>
      ))}
      {!isStarter && myResponse && (
        <div className="rc-chips">
          {CHOICES.map((c) => {
            const active = myResponse.status === c.status;
            return (
              <button
                key={c.status}
                className={`rc-chip ${active ? 'rc-chip-active' : ''}`}
                style={
                  active
                    ? { background: c.tone, borderColor: c.tone, color: colors.bg }
                    : { color: c.tone, borderColor: colors.divider }
                }
                disabled={submitting !== null}
                onClick={() => respond(c.status)}
              >
                {statusLabel(c.status)}
              </button>
            );
          })}
        </div>
      )}
      {otherResponses.length > 0 && isStarter && (
        <div className="rc-tally">
          {otherResponses.map((r) => {
            const name = crew.find((c) => c.id === r.user_id)?.display_name ?? '?';
            return (
              <div key={r.user_id} className="rc-tally-row">
                {name} · {statusLabel(r.status)}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .rc-banner {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 120px);
          left: 12px; right: 12px;
          background: ${colors.surface};
          border: 1px solid ${colors.accent};
          border-radius: 12px;
          padding: 12px;
          display: flex; flex-direction: column; gap: 4px;
          z-index: 1200;
        }
        .rc-line { color: ${colors.text}; font-size: 13px; font-weight: 600; }
        .rc-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
        .rc-chip {
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid;
          background: ${colors.bg};
          font-size: 12px;
          font-weight: 600;
        }
        .rc-chip-active { font-weight: 700; }
        .rc-chip:disabled { opacity: 0.5; }
        .rc-tally { margin-top: 8px; display: flex; flex-direction: column; gap: 3px; }
        .rc-tally-row { color: ${colors.textMuted}; font-size: 12px; }
      `}</style>
    </div>
  );
}
